// Provenance: repo:tds-reconciliation-portal:frontend/src/api/upload.ts (ADR-0005) — the XHR
// body is the predecessor's (fetch cannot report upload progress); the auth changed worlds:
// Bearer + the org header instead of cookies, one silent refresh via the shell's session store.
import { ApiError, apiUrl } from './client'
import { getOrgId } from '../lib/api'
import { session } from '../lib/session'
import type { Upload } from './types'

export interface UploadProgress {
  /** 0..1 transfer fraction; 1 while the server is still parsing. */
  fraction: number
  loaded: number
  total: number
  /** Rolling-average bytes/second (0 until measurable). */
  rate: number
}

function once(
  url: string,
  file: File,
  onProgress: (p: UploadProgress) => void,
  password?: string,
) {
  return new Promise<{ status: number; body: string }>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', url)
    const token = session.getSession()?.accessToken
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`)
    const org = getOrgId()
    if (org) xhr.setRequestHeader('X-Fiveox-Org', org)
    const t0 = Date.now()
    xhr.upload.onprogress = (e) => {
      if (!e.lengthComputable) return
      const secs = (Date.now() - t0) / 1000
      onProgress({
        fraction: e.total ? e.loaded / e.total : 0,
        loaded: e.loaded,
        total: e.total,
        rate: secs > 0.2 ? e.loaded / secs : 0,
      })
    }
    xhr.onerror = () => reject(new ApiError('NETWORK', 'Network error during upload', 0))
    xhr.onload = () => resolve({ status: xhr.status, body: xhr.responseText })
    const fd = new FormData()
    fd.append('file', file)
    if (password) fd.append('password', password) // in-memory only, never stored
    xhr.send(fd)
  })
}

export async function uploadWithProgress(
  periodId: string,
  kind: 'source' | 'book' | 'evidence',
  file: File,
  onProgress: (p: UploadProgress) => void,
  password?: string,
): Promise<Upload> {
  const url = apiUrl(`/periods/${periodId}/uploads?kind=${kind}`)
  let res = await once(url, file, onProgress, password)
  if (res.status === 401 && (await session.refreshSession())) {
    res = await once(url, file, onProgress, password)
  }
  let parsed: unknown = null
  try {
    parsed = JSON.parse(res.body)
  } catch {
    /* non-JSON body */
  }
  const envelope = parsed as {
    success?: boolean
    data?: Upload
    error?: { code?: string; message?: string }
  } | null
  if (res.status < 200 || res.status >= 300 || !envelope?.success) {
    throw new ApiError(
      envelope?.error?.code ?? 'INTERNAL',
      envelope?.error?.message ?? `HTTP ${res.status}`,
      res.status,
    )
  }
  return envelope.data as Upload
}
